// Package imports:
import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { faCaretDown, faCaretUp, faCircle, faEdit, faPlusCircle } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import Toast from "react-bootstrap/esm/Toast";
import ToastContainer from "react-bootstrap/esm/ToastContainer";
// Component imports:
import Alert, { AlertType } from "../../ui-elements/Alert/Alert";
import Button from "../../ui-elements/Button/Button";
import DropSelect from "../../ui-elements/DropSelect/DropSelect";
import Input from "../../ui-elements/Input/Input";
import Table from "../../ui-elements/Table/Table";
import Link from "../../ui-elements/Link/Link";
import CompanyWatchAddModal from "./CompanyWatchModals/CompanyWatchAddModal";
import CompanyWatchEditModal from "./CompanyWatchModals/CompanyWatchEditModal";
// Service imports:
import { GET_KELDAN_API_URL, TOAST_DELAY } from "../../services/config";
import { insertDash } from "../../services/utils";
// Type imports:
import {
  CompanyWatchItem,
  CompanyFilterState,
  CompanyPepFilter,
  CompanySanctionsFilter,
  CompanyConnectionsFilter,
} from "../../types/CompanyWatchTypes";
import { IKeldanApiResponse } from "../../types/KeldanTypes";
import Bugsnag from "@bugsnag/js";
import { IPepWatchResponse } from "./PepWatch";
import { PaginationDropdown, PaginationFlipper, useChunkedPagination } from "../../services/paginationHooks";

interface CompanyWatchTableSectionProps {
  isDisabled?: boolean;
}

type SortBy = "name" | null;
type SortOrder = "asc" | "desc" | null;

const CHUNK_SIZE = 500;

const CompanyWatchTableSection: React.FC<CompanyWatchTableSectionProps> = ({ isDisabled = true }) => {
  // Toast states
  const [showToast, setToastShow] = useState(false);
  const [toastMessage, setToastMessage] = useState<string>("");
  const [toastType, setToastType] = useState<AlertType>("success");

  // Modal states
  const [addModal_isOpen, showAddModal] = useState(false);
  const [editModal_isOpen, showEditModal] = useState(false);
  const [editModal_data, setEditModal_data] = useState<CompanyWatchItem | undefined>(undefined);

  // Data and loading states
  const [isExcelLoading, setIsExcelLoading] = useState<boolean>(false);
  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true);
  const [loadedData, setLoadedData] = useState<CompanyWatchItem[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);

  // Consolidated filter state
  const [filters, setFilters] = useState<CompanyFilterState>({
    searchTerm: "",
    pepStatus: "showAll",
    sanctionsStatus: "showAll",
    connectionsFilter: "showAll",
    sortBy: null,
    sortOrder: null,
  });

  const [searchInputValue, setSearchInputValue] = useState<string>("");
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const currentFiltersRef = useRef<string>("");
  const hasInitializedRef = useRef<boolean>(false);

  // Server-side sorting handler
  const handleServerSort = useCallback((columnSortBy: SortBy) => {
    if (!columnSortBy) return;
    setFilters((prev) => {
      if (prev.sortBy === columnSortBy) {
        if (prev.sortOrder === "asc") return { ...prev, sortOrder: "desc" as SortOrder };
        if (prev.sortOrder === "desc") return { ...prev, sortBy: null, sortOrder: null };
        return { ...prev, sortOrder: "asc" as SortOrder };
      }
      return { ...prev, sortBy: columnSortBy, sortOrder: "asc" as SortOrder };
    });
  }, []);

  const getSortIndicator = useCallback(
    (columnSortBy: SortBy): JSX.Element | null => {
      if (filters.sortBy === columnSortBy) {
        if (filters.sortOrder === "asc")
          return (
            <span className={cx("caret-span", { "is-ascending": true })}>
              <FontAwesomeIcon icon={faCaretUp} className="sort-indicator" />
            </span>
          );
        if (filters.sortOrder === "desc")
          return (
            <span className={cx("caret-span", { "is-descending": true })}>
              <FontAwesomeIcon icon={faCaretDown} className="sort-indicator" />
            </span>
          );
      }
      return null;
    },
    [filters.sortBy, filters.sortOrder]
  );

  const SortableColumnHeader: React.FC<{
    title: string;
    sortBy: SortBy;
    onClick: (sortBy: SortBy) => void;
    getSortIndicator: (sortBy: SortBy) => JSX.Element | null;
    disabled?: boolean;
  }> = ({ title, sortBy, onClick, getSortIndicator: getIndicator, disabled = false }) => {
    const isLoading = isInitialLoading || pagination.isLoadingMore;
    return (
      <div
        onClick={disabled || isLoading ? undefined : () => onClick(sortBy)}
        className={cx("pep-sortable-header", { "pep-sortable-header--disabled": disabled || isLoading })}
      >
        <span className="pep-sortable-header__label">
          {title} {getIndicator(sortBy)}
        </span>
      </div>
    );
  };

  // Build filter query string
  function buildFilterString(): string {
    let url = "";
    if (filters.searchTerm.trim() !== "") {
      url += `&searchTerm=${encodeURIComponent(filters.searchTerm)}`;
    }
    if (filters.pepStatus !== "showAll") {
      url += `&filterPep=${filters.pepStatus}`;
    }
    if (filters.sanctionsStatus !== "showAll") {
      url += `&filterSanctions=${filters.sanctionsStatus}`;
    }
    if (filters.connectionsFilter !== "showAll") {
      url += `&filterConnections=${filters.connectionsFilter}`;
    }
    if (filters.sortBy) {
      url += `&sortBy=${filters.sortBy}`;
    }
    if (filters.sortOrder) {
      url += `&order=${filters.sortOrder}`;
    }
    return url;
  }

  const handleExcelDownload = async (e: React.MouseEvent) => {
    e.preventDefault();
    setIsExcelLoading(true);
    try {
      const response = await fetch(`/Excel/CompanyWatchList?${buildFilterString()}`);
      const disposition = response.headers.get('Content-Disposition');
      const filename = disposition?.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)?.[1]?.replace(/['"]/g, '') ?? 'CompanyWatchList.xlsx';
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setIsExcelLoading(false);
    }
  };

  // API fetch function for chunked data loading
  const fetchChunkedData = useCallback(
    async (startIndex: number, chunkSize: number): Promise<CompanyWatchItem[]> => {
      try {
        let url = `${GET_KELDAN_API_URL()}/Pep/GetCompanyWatchList?start=${startIndex}&limit=${chunkSize}&includeCount=true`;
        const filterString = buildFilterString();
        if (filterString.length > 0) {
          url += filterString;
        }

        const response = await fetch(url, {
          method: "GET",
          headers: { "Content-Type": "application/json; charset=utf-8" },
        });
        const responseBody: IKeldanApiResponse<IPepWatchResponse<CompanyWatchItem[]>> = await response.json();

        if (response.ok && responseBody.result) {
          if (startIndex === 0) {
            setTotalCount(responseBody.result.count || 0);
          }
          return responseBody.result.data || [];
        } else {
          throw new Error(responseBody.message || "Failed to fetch data");
        }
      } catch (error) {
        console.error("Error fetching company watch chunk:", error);
        if (error instanceof Error) {
          Bugsnag.notify(error);
          setToastMessage(error.message);
          setToastType("error");
          setToastShow(true);
        }
        return [];
      }
    },
    [filters]
  );

  const lastFetchedIndexRef = useRef<number>(0);

  const handleFetchMoreData = useCallback(
    async (startIndex: number, chunkSize: number): Promise<CompanyWatchItem[]> => {
      if (startIndex < lastFetchedIndexRef.current) return [];
      if (startIndex >= totalCount && totalCount > 0) return [];

      const newData = await fetchChunkedData(startIndex, chunkSize);
      if (newData.length > 0) {
        lastFetchedIndexRef.current = startIndex + newData.length;
        setLoadedData((prevData) => [...prevData, ...newData]);
      }
      return newData;
    },
    [fetchChunkedData, totalCount]
  );

  const handleFiltersChange = useCallback(() => {
    setLoadedData([]);
    setTotalCount(0);
    lastFetchedIndexRef.current = 0;
  }, []);

  const paginationConfig = useMemo(
    () => ({
      chunkSize: CHUNK_SIZE,
      onFetchMoreData: handleFetchMoreData,
      onFiltersChange: handleFiltersChange,
      prefetchThreshold: 3,
    }),
    [handleFetchMoreData, handleFiltersChange]
  );

  const pagination = useChunkedPagination<CompanyWatchItem>(
    totalCount,
    loadedData,
    paginationConfig,
    10,
    0
  );

  const loadInitialData = useCallback(async () => {
    try {
      setIsInitialLoading(true);
      setLoadedData([]);
      const initialData = await fetchChunkedData(0, CHUNK_SIZE);
      setLoadedData(initialData);
    } catch (error) {
      console.error("Error loading initial company watch data:", error);
    } finally {
      setIsInitialLoading(false);
    }
  }, [fetchChunkedData]);

  // Debounced search
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = e.target.value;
    setSearchInputValue(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setFilters((prev) => ({ ...prev, searchTerm: value }));
    }, 500);
  };

  const handlePepStatusFilterChange = (option: string) => {
    setFilters((prev) => ({ ...prev, pepStatus: option as CompanyPepFilter }));
  };

  const handleSanctionsStatusFilterChange = (option: string) => {
    setFilters((prev) => ({ ...prev, sanctionsStatus: option as CompanySanctionsFilter }));
  };

  const handleConnectionsFilterChange = (option: string) => {
    setFilters((prev) => ({ ...prev, connectionsFilter: option as CompanyConnectionsFilter }));
  };

  // Initial data load
  useEffect(() => {
    if (isDisabled) {
      setIsInitialLoading(false);
      return;
    }
    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
      loadInitialData();
    }
  }, [isDisabled]);

  // Filter change effect
  useEffect(() => {
    if (isDisabled) return;
    const filtersSignature = JSON.stringify(filters);
    if (filtersSignature !== currentFiltersRef.current && hasInitializedRef.current) {
      currentFiltersRef.current = filtersSignature;
      loadInitialData();
    } else if (!hasInitializedRef.current) {
      currentFiltersRef.current = filtersSignature;
    }
  }, [filters, loadInitialData, isDisabled]);

  // Disabled state change effect
  useEffect(() => {
    if (isDisabled) {
      setLoadedData([]);
      setTotalCount(0);
      setIsInitialLoading(false);
      hasInitializedRef.current = false;
    } else if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
      loadInitialData();
    }
  }, [isDisabled, loadInitialData]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, []);

  const handleModalSuccess = useCallback(() => {
    loadInitialData();
  }, [loadInitialData]);

  return (
    <>
      <div className="filter-wrapper">
        <Input
          disabled={isDisabled}
          onChange={handleSearchChange}
          value={searchInputValue}
          className="input-field"
          label="Leita"
          withSearchGlassIcon
        />
        <div className="dropdown-field">
          <DropSelect
            disabled={isDisabled}
            className="dropdown-field"
            label="Stjórnmálaleg tengsl"
            options={[
              { label: "Sýna allt", value: "showAll" },
              { label: "Já", value: "pep" },
              { label: "Nei", value: "notPep" },
              { label: "Í vöktun", value: "active" },
              { label: "Ekki í vöktun", value: "inactive" },
            ]}
            defaultValue="showAll"
            onChange={handlePepStatusFilterChange}
            size="lg"
          />
        </div>
        <div className="dropdown-field">
          <DropSelect
            disabled={isDisabled}
            className="dropdown-field"
            label="Alþjóðlegir válistar"
            options={[
              { label: "Sýna allt", value: "showAll" },
              { label: "Já", value: "sanctions" },
              { label: "Nei", value: "notSanctions" },
              { label: "Í vöktun", value: "active" },
              { label: "Ekki í vöktun", value: "inactive" },
            ]}
            defaultValue="showAll"
            onChange={handleSanctionsStatusFilterChange}
            size="lg"
          />
        </div>
        <div className="dropdown-field">
          <DropSelect
            disabled={isDisabled}
            className="dropdown-field"
            label="Tengingar"
            options={[
              { label: "Sýna allt", value: "showAll" },
              { label: "Í vöktun", value: "active" },
              { label: "Ekki í vöktun", value: "inactive" },
              { label: "Á listum", value: "flagged" },
              { label: "Ekki á listum", value: "notFlagged" },
            ]}
            defaultValue="showAll"
            onChange={handleConnectionsFilterChange}
            size="lg"
          />
        </div>
      </div>
      <>
        <Table
          showLoading={
            isInitialLoading ||
            (pagination.isLoadingMore &&
              pagination.loadedItemsCount - pagination.itemsPerPage <
                pagination.currentPageIndex * pagination.itemsPerPage)
          }
          tableSize="lg"
          data={pagination.currentData}
          showEmptyTableText
          aSide={
            <Button
              disabled={isDisabled}
              size="lg"
              icon={faPlusCircle}
              onClick={() => showAddModal(true)}
              buttonType="secondary"
            >
              Bæta við lögaðilum
            </Button>
          }
          bSide={
            <>
              <Link icon="excel" disabled={isDisabled || isExcelLoading} onClick={handleExcelDownload}>
                Gögn í Excel
              </Link>
              <PaginationDropdown
                disabled={isDisabled}
                paginationConfig={{ usePortal: true }}
                paginationState={pagination}
              />
            </>
          }
          rowClassName={(item) => {
            if (item.pepFrequency === "never" && item.sanctionsFrequency === "never") {
              return "row-inactive";
            }
          }}
          columns={[
            {
              title: (
                <SortableColumnHeader
                  title="Nafn Lögaðila"
                  sortBy="name"
                  onClick={handleServerSort}
                  getSortIndicator={getSortIndicator}
                  disabled={isDisabled}
                />
              ),
              renderCell: (item) => {
                const isActive = item.pepFrequency !== "never" || item.sanctionsFrequency !== "never";
                return (
                  <div className="pep-name-cell">
                    <FontAwesomeIcon
                      icon={faEdit}
                      className="fa-edit-icon"
                      onClick={() => {
                        setEditModal_data(item);
                        showEditModal(true);
                      }}
                    />
                    <span>{item.name}</span>
                    <FontAwesomeIcon
                      icon={faCircle}
                      className={cx("pep-status-dot", {
                        "pep-status-dot--active": isActive,
                        "pep-status-dot--inactive": !isActive,
                      })}
                    />
                  </div>
                );
              },
              textAlign: "left",
              minColumnWidth: "230px",
              serverSideSortable: true,
            },
            {
              title: "Kennitala",
              renderCell: ({ id: { companyId } }) => insertDash(companyId),
              textAlign: "left",
            },
            {
              title: "Fjöldi í vöktun",
              renderCell: ({ connectionCount }) => connectionCount ?? 0,
              textAlign: "left",
            },
            {
              title: "Fjöldi á listum",
              renderCell: ({ pepOrSanctionsCount }) => pepOrSanctionsCount ?? 0,
              textAlign: "left",
            },
            {
              title: "Skýrsla",
              renderCell: (item) => (
                <Button
                  buttonType="primary"
                  size="sm"
                  anchorProps={{ href: `/Valistar/Logadila-Voktun-Skyrsla/${item.id.companyId}` }}
                >
                  Sækja
                </Button>
              ),
            },
          ]}
        />
        <PaginationFlipper paginationState={pagination} />
      </>

      {/* Modals */}
      <CompanyWatchAddModal
        setToastMessage={setToastMessage}
        setToastType={setToastType}
        setToastShow={setToastShow}
        show={addModal_isOpen}
        close={() => showAddModal(false)}
        onSuccess={handleModalSuccess}
      />
      <CompanyWatchEditModal
        setToastMessage={setToastMessage}
        setToastType={setToastType}
        setToastShow={setToastShow}
        data={editModal_data}
        show={editModal_isOpen}
        close={() => showEditModal(false)}
        onSuccess={handleModalSuccess}
      />

      {/* Toast */}
      <ToastContainer className="toastContainer">
        <Toast onClose={() => setToastShow(false)} autohide delay={TOAST_DELAY} show={showToast}>
          <Alert type={toastType} headText={toastMessage} />
        </Toast>
      </ToastContainer>
    </>
  );
};

export default CompanyWatchTableSection;
